import React from 'react';

export const Avatar = ({ initials, gradient, size = 36 }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: size * 0.33, flexShrink: 0 }}>
    {initials}
  </div>
);

export const Chip = ({ label, color, bg, border, style = {} }) => (
  <span style={{ background: bg, color, border: `0.5px solid ${border}`, padding: '3px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, display: 'inline-flex', ...style }}>
    {label}
  </span>
);

export const Btn = ({ t, label, onClick, bg, color, disabled, style = {}, icon }) => (
  <button onClick={disabled ? undefined : onClick} style={{ background: disabled ? (t.isDark ? '#333' : '#e0e0e0') : (bg || t.primary2), color: disabled ? t.textHint : (color || '#fff'), borderRadius: 10, padding: '12px 16px', textAlign: 'center', fontSize: 14, fontWeight: 800, width: '100%', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, ...style }}>
    {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
    {label}
  </button>
);

export const Row = ({ t, label, value, valueColor, last }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: last ? 'none' : `0.5px solid ${t.border}`, fontSize: 13 }}>
    <span style={{ color: t.textSec, fontWeight: 600 }}>{label}</span>
    <span style={{ color: valueColor || t.text, fontWeight: 700 }}>{value}</span>
  </div>
);

export const Card = ({ t, children, style = {} }) => (
  <div style={{ background: t.surface, borderRadius: 14, padding: '14px 16px', border: `0.5px solid ${t.border}`, ...style }}>
    {children}
  </div>
);

export const SectionTitle = ({ t, label, right }) => (
  <div style={{ fontSize: 11, fontWeight: 800, color: t.primary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span>{label}</span>{right}
  </div>
);

export const StatBox = ({ t, value, label, color, bg, border }) => (
  <div style={{ background: t.isDark ? t.surface : (bg || t.primaryLight), borderRadius: 12, padding: '12px 10px', border: `0.5px solid ${t.isDark ? t.border : (border || t.borderLight)}`, textAlign: 'center', flex: 1 }}>
    <div style={{ fontSize: 18, fontWeight: 900, color: t.isDark ? (color || t.primary) : (color || t.primary) }}>{value}</div>
    <div style={{ fontSize: 11, color: t.textSec, fontWeight: 600, marginTop: 2 }}>{label}</div>
  </div>
);

export const ProgressBar = ({ t, value, max, color, height = 6 }) => {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div style={{ background: t.isDark ? '#1a2e1a' : '#f0f0f0', borderRadius: height, height, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color || t.primary, borderRadius: height, transition: 'width 0.4s ease' }} />
    </div>
  );
};

export const Toggle = ({ t, value, onChange }) => (
  <div onClick={() => onChange(!value)} style={{ width: 44, height: 24, borderRadius: 12, background: value ? t.primary : (t.isDark ? '#333' : '#e0e0e0'), position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}>
    <div style={{ position: 'absolute', width: 18, height: 18, borderRadius: '50%', background: '#fff', top: 3, left: value ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }} />
  </div>
);

export const Input = ({ t, label, value, onChange, placeholder, type = 'text', required, hint }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', fontSize: 12, color: t.isDark ? t.primary : '#555', fontWeight: 700, marginBottom: 4 }}>{label}{required && <span style={{ color: t.red }}> *</span>}</label>}
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: '100%', background: t.isDark ? t.surface2 : '#F5F5F5', border: `0.5px solid ${t.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: t.text, fontFamily: 'Nunito, sans-serif', fontWeight: 600 }}
    />
    {hint && <div style={{ fontSize: 11, color: t.textHint, marginTop: 3, fontWeight: 500 }}>{hint}</div>}
  </div>
);

export const ValidationInput = ({ t, label, value, onChange, placeholder, result, hint }) => (
  <div style={{ marginBottom: 8 }}>
    {label && <label style={{ display: 'block', fontSize: 12, color: t.isDark ? t.primary : '#555', fontWeight: 700, marginBottom: 4 }}>{label}<span style={{ color: t.red }}> *</span></label>}
    <div style={{ background: result ? (result.ok ? t.primaryLight : t.redLight) : (t.isDark ? t.surface2 : '#F5F5F5'), borderRadius: 8, padding: '10px 12px', border: `${result ? 1.5 : 0.5}px solid ${result ? (result.ok ? t.primary : t.red) : t.border}`, display: 'flex', alignItems: 'center', gap: 8, transition: 'border-color 0.2s' }}>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: t.text, flex: 1, fontFamily: 'Nunito, sans-serif', fontWeight: 600 }} />
      {result && <span style={{ fontSize: 18, flexShrink: 0 }}>{result.ok ? '✅' : '⚠️'}</span>}
    </div>
    {result && <div style={{ fontSize: 11, color: result.ok ? t.primary : t.red, fontWeight: 700, marginTop: 3 }}>{result.msg}</div>}
    {!result && hint && <div style={{ fontSize: 11, color: t.textHint, marginTop: 3, fontWeight: 500 }}>{hint}</div>}
  </div>
);

export const PageHeader = ({ t, title, onBack, right, bgColor }) => (
  <div style={{ background: bgColor || t.primary2, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
    {onBack && <button onClick={onBack} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18, color: '#fff', flexShrink: 0 }}>←</button>}
    <span style={{ color: '#fff', fontSize: 16, fontWeight: 800, flex: 1 }}>{title}</span>
    {right}
  </div>
);

export const EmptyState = ({ t, icon, title, subtitle }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
    <div style={{ fontSize: 48, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 6 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 13, color: t.textSec, fontWeight: 500, lineHeight: 1.5 }}>{subtitle}</div>}
  </div>
);

export const Badge = ({ label, color, bg, border }) => (
  <div style={{ background: bg, border: `0.5px solid ${border}`, borderRadius: 8, padding: '3px 8px', fontSize: 11, fontWeight: 800, color, display: 'inline-flex', alignItems: 'center' }}>{label}</div>
);

export const Divider = ({ t }) => <div style={{ height: 0.5, background: t.border, margin: '4px 0' }} />;
